import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von10Outcomes } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von10OutcomesFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von10OutcomesForm: React.FC<Von10OutcomesFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  // Initial disposition
  const [initDispoDate, setInitDispoDate] = useState<string>('');

  // Final discharge
  const [finalDischarge, setFinalDischarge] = useState<string>('');
  const [transferReasonOut, setTransferReasonOut] = useState<string>('');
  const [finalDischargeDate, setFinalDischargeDate] = useState<string>('');
  const [weightGDischarge, setWeightGDischarge] = useState<string>('');
  const [daysDischarge, setDaysDischarge] = useState<string>('');

  // Support at discharge
  const [oxDischarge, setOxDischarge] = useState(false);
  const [ventDischarge, setVentDischarge] = useState(false);
  const [hfvDischarge, setHfvDischarge] = useState(false);
  const [ncfDischarge, setNcfDischarge] = useState(false);
  const [ncfDischargeGt2l, setNcfDischargeGt2l] = useState(false);
  const [nimvDischarge, setNimvDischarge] = useState(false);
  const [cpapDischarge, setCpapDischarge] = useState(false);
  const [monitorDischarge, setMonitorDischarge] = useState(false);
  const [enteralFeedDischarge, setEnteralFeedDischarge] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getOutcomes(patientId);

      if (data) {
        setHasExistingData(true);
        setInitDispoDate(data.init_dispo_date || '');
        setFinalDischarge(data.final_discharge?.toString() || '');
        setTransferReasonOut(data.transfer_reason_out?.toString() || '');
        setFinalDischargeDate(data.final_discharge_date || '');
        setWeightGDischarge(data.weight_g_discharge?.toString() || '');
        setDaysDischarge(data.days_discharge?.toString() || '');

        setOxDischarge(Boolean(data.ox_discharge));
        setVentDischarge(Boolean(data.vent_discharge));
        setHfvDischarge(Boolean(data.hfv_discharge));
        setNcfDischarge(Boolean(data.ncf_discharge));
        setNcfDischargeGt2l(Boolean(data.ncf_discharge_gt2l));
        setNimvDischarge(Boolean(data.nimv_discharge));
        setCpapDischarge(Boolean(data.cpap_discharge));
        setMonitorDischarge(Boolean(data.monitor_discharge));
        setEnteralFeedDischarge(data.enteral_feed_discharge || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    if (finalDischarge === '4') {
      toast({ title: 'Selectați starea copilului', description: 'Starea înregistrată anterior nu mai este disponibilă. Alegeți starea actuală înainte de salvare.', variant: 'destructive' });
      return;
    }

    try {
      setSubmitting(true);

      const formData: Partial<Von10Outcomes> = {
        patient_id: patientId,
        init_dispo_date: initDispoDate || undefined,
        final_discharge: finalDischarge ? parseInt(finalDischarge) : undefined,
        transfer_reason_out: transferReasonOut ? parseInt(transferReasonOut) : undefined,
        final_discharge_date: finalDischargeDate || undefined,
        weight_g_discharge: weightGDischarge ? parseInt(weightGDischarge) : undefined,
        days_discharge: daysDischarge ? parseInt(daysDischarge) : undefined,

        ox_discharge: oxDischarge,
        vent_discharge: ventDischarge,
        hfv_discharge: hfvDischarge,
        ncf_discharge: ncfDischarge,
        ncf_discharge_gt2l: ncfDischargeGt2l,
        nimv_discharge: nimvDischarge,
        cpap_discharge: cpapDischarge,
        monitor_discharge: monitorDischarge,
        enteral_feed_discharge: enteralFeedDischarge || undefined
      };

      if (hasExistingData) {
        await vonDataService.updateOutcomes(patientId, formData);
      } else {
        await vonDataService.createOutcomes(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 14: Rezultate externare</CardTitle>
        <CardDescription>Date despre externare</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Initial disposition */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Situația la externare</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="finalDischarge">Stare copil</Label>
                <Select value={finalDischarge === '4' ? '' : finalDischarge} onValueChange={setFinalDischarge} disabled={readOnly}>
                  <SelectTrigger id="finalDischarge">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Acasă</SelectItem>
                    <SelectItem value="2">Decedat</SelectItem>
                    <SelectItem value="3">Transferat la alt spital</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="initDispoDate">Data externării</Label>
                <Input
                  id="initDispoDate"
                  type="date"
                  value={initDispoDate}
                  onChange={(e) => setInitDispoDate(e.target.value)}
                  disabled={readOnly}
                />
              </div>

            </div>
          </div>

          {/* Final discharge */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Date la externare</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="finalDischargeDate">Data externării finale</Label>
                <Input
                  id="finalDischargeDate"
                  type="date"
                  value={finalDischargeDate}
                  onChange={(e) => setFinalDischargeDate(e.target.value)}
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="weightGDischarge">Greutate la externare (grame)</Label>
                <Input
                  id="weightGDischarge"
                  type="number"
                  value={weightGDischarge}
                  onChange={(e) => setWeightGDischarge(e.target.value)}
                  placeholder="Grame"
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="daysDischarge">Durata internării (zile)</Label>
                <Input
                  id="daysDischarge"
                  type="number"
                  value={daysDischarge}
                  onChange={(e) => setDaysDischarge(e.target.value)}
                  placeholder="Zile"
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* Support at discharge */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Suport la externare</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="oxDischarge"
                  checked={oxDischarge}
                  onCheckedChange={(checked) => setOxDischarge(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="oxDischarge" className="text-sm font-normal cursor-pointer">
                  Oxigen la externare
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ventDischarge"
                  checked={ventDischarge}
                  onCheckedChange={(checked) => setVentDischarge(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ventDischarge" className="text-sm font-normal cursor-pointer">
                  Ventilație convențională la externare
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hfvDischarge"
                  checked={hfvDischarge}
                  onCheckedChange={(checked) => setHfvDischarge(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="hfvDischarge" className="text-sm font-normal cursor-pointer">
                  HFV la externare
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ncfDischarge"
                  checked={ncfDischarge}
                  onCheckedChange={(checked) => setNcfDischarge(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ncfDischarge" className="text-sm font-normal cursor-pointer">
                  Canulă nazală la externare
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ncfDischargeGt2l"
                  checked={ncfDischargeGt2l}
                  onCheckedChange={(checked) => setNcfDischargeGt2l(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="ncfDischargeGt2l" className="text-sm font-normal cursor-pointer">
                  Flux nazal {'>'} 2 L/min la externare
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="nimvDischarge"
                  checked={nimvDischarge}
                  onCheckedChange={(checked) => setNimvDischarge(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="nimvDischarge" className="text-sm font-normal cursor-pointer">
                  Ventilație nazală la externare
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cpapDischarge"
                  checked={cpapDischarge}
                  onCheckedChange={(checked) => setCpapDischarge(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="cpapDischarge" className="text-sm font-normal cursor-pointer">
                  CPAP nazal la externare
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="monitorDischarge"
                  checked={monitorDischarge}
                  onCheckedChange={(checked) => setMonitorDischarge(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="monitorDischarge" className="text-sm font-normal cursor-pointer">
                  Monitor la externare
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="enteralFeedDischarge">Nutriție enterală la externare</Label>
              <Input
                id="enteralFeedDischarge"
                value={enteralFeedDischarge}
                onChange={(e) => setEnteralFeedDischarge(e.target.value)}
                placeholder="Descriere nutriție enterală"
                disabled={readOnly}
              />
            </div>
          </div>

            <div className="space-y-2">
              <Label htmlFor="transferReasonOut">Motivele externării</Label>
              <Select value={transferReasonOut} onValueChange={setTransferReasonOut} disabled={readOnly}>
                <SelectTrigger id="transferReasonOut">
                  <SelectValue placeholder="Selectează..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Chirurgie</SelectItem>
                  <SelectItem value="2">Hipotermie terapeutică</SelectItem>
                  <SelectItem value="3">Malnutriție</SelectItem>
                  <SelectItem value="4">Servicii medicale de diagnostic și tratament</SelectItem>
                  <SelectItem value="5">Îngrijire cronică</SelectItem>
                  <SelectItem value="6">ECMO</SelectItem>
                  <SelectItem value="7">Altele</SelectItem>
                </SelectContent>
              </Select>
            </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
